/**
 * RevealOrchestrator — the paced reveal of a session's result (roadmap S-4,
 * ruling R10). The host starts it; everyone following sees a countdown, then
 * the ranks in order as the host advances. The state lives in
 * `useSessionReveal`, which the TV board reads too.
 *
 * Keys for the host: R starts, Space shows the next rank, A shows the rest
 * (the one key that skips the pacing).
 *
 * Props:
 *   isFacilitator — shows the host's controls if true
 *   items         — the full ranked result (rank ascending; tied items share a rank)
 */

import { useEffect, useState } from 'react';
import { revealStateFromEvents, useSessionReveal, type RevealEventRow } from '@/hooks/useSessionReveal';
import { C } from '../../theme/participantTokens';
import { formatRank } from '@/lib/itemRankings';

interface RevealItem {
  rank: number;
  title: string;
  score: number;
}

interface RevealOrchestratorProps {
  sessionId: string;
  items: RevealItem[];
  isFacilitator?: boolean;
  /**
   * Set for a guest by link (S-8), who cannot read the room's events: the
   * reveal events from the guest's room read. The reveal follows them; nothing
   * is subscribed and there are no host controls.
   */
  events?: readonly RevealEventRow[];
}

function typingInField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
}

export function RevealOrchestrator({
  sessionId,
  items,
  isFacilitator: isFacilitatorProp = false,
  events,
}: RevealOrchestratorProps) {
  const followed = events !== undefined;
  const isFacilitator = isFacilitatorProp && !followed;
  const live = useSessionReveal(followed ? null : sessionId, items.length);
  // A followed reveal ticks so its countdown moves between reads.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!followed) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [followed]);
  const { phase, countdown, revealedUpTo } = followed ? revealStateFromEvents(events, items.length, now) : live;
  const { start: startReveal, next: advanceRank, showAll: completeReveal } = live;

  useEffect(() => {
    if (!isFacilitator) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typingInField(e.target)) return;
      const key = e.key.toLowerCase();
      if (key === 'r' && (phase === 'idle' || phase === 'complete')) {
        e.preventDefault();
        void startReveal();
      } else if (e.key === ' ' && phase === 'revealing') {
        e.preventDefault();
        void advanceRank();
      } else if (key === 'a' && phase !== 'complete') {
        e.preventDefault();
        void completeReveal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFacilitator, phase, startReveal, advanceRank, completeReveal]);

  // Idle state — facilitator sees Start button, participants see waiting message
  if (phase === 'idle') {
    return (
      <div
        style={{
          background: C.surface,
          border: `1px solid ${C.rule}`,
          borderRadius: 4,
          padding: '28px 24px',
          textAlign: 'center',
        }}
      >
        {isFacilitator ? (
          <>
            <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.1rem', fontWeight: 600, color: C.textPri, marginBottom: 8 }}>
              Ready to reveal results?
            </div>
            <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.82rem', color: C.textTer, marginBottom: 20 }}>
              Everyone following — the Board and each person's result page — sees a countdown, then the ranks in order as you advance. Keys: R starts, Space shows the next, A shows the rest.
            </div>
            <button
              type="button"
              onClick={() => void startReveal()}
              style={{
                padding: '10px 28px', borderRadius: 6,
                border: `1px solid ${C.mustard}`,
                background: C.mustard, color: C.bgRaised,
                fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem',
                letterSpacing: '0.14em', textTransform: 'uppercase',
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              Start reveal
            </button>
            <button
              type="button"
              onClick={() => void completeReveal()}
              style={{
                marginLeft: 8, padding: '10px 18px', borderRadius: 6,
                border: `1px solid ${C.ruleBright}`, background: 'transparent',
                color: C.textTer, fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.66rem', cursor: 'pointer',
              }}
            >
              Show it all
            </button>
          </>
        ) : (
          <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', color: C.textTer }}>
            The result is ready. The host reveals it on the Board, and it appears here as it is revealed.
          </div>
        )}
      </div>
    );
  }

  // Countdown
  if (phase === 'countdown') {
    return (
      <div
        style={{
          background: C.bgRaised,
          border: `1px solid ${C.rule}`,
          borderRadius: 4,
          padding: '48px 24px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '0.58rem',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: C.textDim,
          }}
        >
          Revealing in
        </div>
        <div
          key={countdown}
          style={{
            fontFamily: 'Spectral, Georgia, serif',
            fontSize: '5rem',
            fontWeight: 700,
            color: C.mustard,
            lineHeight: 1,
            animation: 'pulse 0.8s ease-out',
          }}
        >
          {countdown > 0 ? countdown : '…'}
        </div>
      </div>
    );
  }

  // Revealing / Complete
  const visibleItems = items.slice(0, revealedUpTo);
  const maxScore = Math.max(...items.map(i => i.score), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Cascade */}
      <div
        style={{
          background: C.surface,
          border: `1px solid ${C.rule}`,
          borderRadius: 4,
          padding: '18px 20px',
        }}
      >
        <div
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: C.textDim,
            marginBottom: 14,
          }}
        >
          Results — {revealedUpTo}/{items.length} revealed · not published yet
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visibleItems.map((item, idx) => {
            const barPct = (item.score / maxScore) * 100;
            const isTop = idx === 0;
            return (
              <div
                key={`${item.rank}-${idx}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '8px 0',
                  borderBottom: `1px solid ${C.rule}`,
                  animation: `fadeInDown 0.35s ease-out ${idx === revealedUpTo - 1 ? '0s' : '0s'} both`,
                  opacity: 1,
                }}
              >
                <div
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: isTop ? '1.2rem' : '0.72rem',
                    fontWeight: 700,
                    color: isTop ? C.mustard : C.textDim,
                    width: 28,
                    flexShrink: 0,
                    textAlign: 'right',
                  }}
                >
                  {items.filter((other) => other.rank === item.rank).length > 1
                    ? formatRank(item.rank, true)
                    : String(item.rank).padStart(2, '0')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: isTop ? 'Spectral, Georgia, serif' : 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                      fontSize: isTop ? '1rem' : '0.85rem',
                      fontWeight: isTop ? 600 : 400,
                      color: isTop ? C.textPri : C.textSec,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: 4,
                    }}
                  >
                    {item.title}
                  </div>
                  <div style={{ height: 3, background: C.ruleBright, borderRadius: 2, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${barPct}%`,
                        background: isTop ? C.mustard : C.slate,
                        borderRadius: 2,
                        transition: 'width 0.6s ease-out',
                        opacity: isTop ? 0.9 : 0.5,
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: isTop ? C.mustard : C.textDim,
                    flexShrink: 0,
                  }}
                >
                  {item.score.toFixed(1)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Facilitator controls */}
      {isFacilitator && phase !== 'complete' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => void advanceRank()}
            disabled={revealedUpTo >= items.length}
            style={{
              flex: 1, padding: '9px 16px', borderRadius: 6,
              border: `1px solid ${C.mustard}`, background: C.mustard,
              color: C.bgRaised, fontFamily: 'JetBrains Mono, monospace',
              fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              fontWeight: 700, cursor: revealedUpTo < items.length ? 'pointer' : 'default',
              opacity: revealedUpTo >= items.length ? 0.4 : 1,
            }}
          >
            Reveal #{revealedUpTo + 1} →
          </button>
          <button
            type="button"
            onClick={() => void completeReveal()}
            style={{
              padding: '9px 14px', borderRadius: 6,
              border: `1px solid ${C.ruleBright}`, background: 'transparent',
              color: C.textTer, fontFamily: 'JetBrains Mono, monospace',
              fontSize: '0.62rem', cursor: 'pointer',
            }}
          >
            Show the rest
          </button>
        </div>
      )}

      {phase === 'complete' && (
        <div
          style={{
            textAlign: 'center',
            fontFamily: 'Spectral, Georgia, serif',
            fontSize: '0.9rem',
            color: C.textSec,
            padding: '12px',
          }}
        >
          All results revealed ✓
        </div>
      )}
    </div>
  );
}
